/**
 * Canonical drive-hierarchy folder names — the single source of truth for
 * reserved top-level folders and the project / system container locations.
 *
 * Project revamp (docs/plans/2026-06-29_PROJECT_REVAMP.md) replaces the
 * pre-revamp `_system` / `_shared` top level with intuitive, capitalized
 * names: Library, Projects, Persons, Shared, System. BOTH sets are exported —
 * the legacy names stay recognized so un-backfilled tenants keep working and
 * the Stage 4 migration can map old → new from one place.
 *
 * Like file-extensions.ts: folder names are CASE-SENSITIVE on disk (the
 * canonical folder is literally `Library`), but reservation checks are
 * case-INSENSITIVE so a user can't create a confusing near-duplicate
 * (`library`, `LIBRARY`).
 *
 * Pure constants + string helpers, no runtime deps — kept in `@papan/types` so
 * every consumer (api, web, files, workspace, org-connections) imports the same
 * canonical names instead of re-hardcoding them.
 */

import { isDriveRootId, isDriveRootRow } from './listed-hidden-shell.js'

const lower = (s: string): string => s.toLowerCase()

/** New top-level folders (canonical spelling, case-sensitive on disk). */
export const ROOT = {
  LIBRARY: 'Library',
  PROJECTS: 'Projects',
  MINIAPPS: 'PDApps', // standalone PDApps: `PDApps` / `Persons/<email>/PDApps` (renamed from `MiniApps`; the old name still resolves via LEGACY_CONTAINER_NAMES)
  PERSONS: 'Persons', // per-member private spaces: `Persons/<email>/Projects`
  SHARED: 'Shared',
  SYSTEM: 'System',
} as const

/**
 * #1318 — what the drive hierarchy (`ensureDriveHierarchy`, features/files) writes for a PERSONAL drive, defined ONCE:
 * the hierarchy writes exactly this, and the squat census (`signUpFileRowSql` / `signUpGrantRowSql`, shared/auth)
 * accepts exactly this as "what sign-up wrote". A second copy would drift: a changed grant on one side and every new
 * squat holds a row the census calls use (a permanent 409 for its victim).
 */
export const PERSONAL_DRIVE_TOP_FOLDERS = [ROOT.LIBRARY, ROOT.PROJECTS, ROOT.MINIAPPS, ROOT.SHARED, ROOT.SYSTEM] as const
/** The top folders opened to the member / viewer roles below the restricted root (Shared / System stay restricted). */
export const DRIVE_OPEN_TOP_FOLDERS = [ROOT.LIBRARY, ROOT.PROJECTS, ROOT.MINIAPPS] as const
/** Every grant the hierarchy writes carries this provenance (unlocked — #787 §7 / D1). */
export const DRIVE_HIERARCHY_GRANT = { grantedBy: 'system@drive-hierarchy', grantedAtLevel: 'folder_admin' } as const
/** The root's one grant: the admin role manages it (not `owner` — base-role pass-through). */
export const DRIVE_ROOT_ROLE_GRANT = { targetType: 'role', targetId: 'admin', permission: 'admin' } as const
/** Each open top folder's role grants. */
export const DRIVE_OPEN_ROLE_GRANTS = [
  { targetType: 'role', targetId: 'member', permission: 'readwrite' },
  { targetType: 'role', targetId: 'viewer', permission: 'readcopy' },
] as const

/** Legacy top-level folders (pre-revamp; still present on un-backfilled tenants). */
export const LEGACY_ROOT = {
  SYSTEM: '_system',
  SHARED: '_shared',
} as const

/**
 * Legacy CONTAINER names — reserved and resolvable, but from a LATER rename than
 * the pre-revamp `LEGACY_ROOT` above, kept separate so the two eras don't
 * tangle. `MiniApps` was renamed to `PDApps` (ROOT.MINIAPPS); existing tenants
 * keep the old folder until the rename sweep runs, so it must stay resolvable
 * (findMiniAppsContainer) and reserved (create denylist). Drop once every
 * environment's rename `--verify` is clean.
 */
export const LEGACY_CONTAINER = {
  MINIAPPS: 'MiniApps',
} as const

/** Legacy intermediate segments that lived under `_system`. */
export const LEGACY_WORKSPACES_SEGMENT = 'workspaces'
export const LEGACY_USERS_SEGMENT = 'users'

export const NEW_ROOT_NAMES: readonly string[] = Object.values(ROOT)
export const LEGACY_ROOT_NAMES: readonly string[] = Object.values(LEGACY_ROOT)
/** Legacy container names (see LEGACY_CONTAINER) — reserved during the rename window. */
export const LEGACY_CONTAINER_NAMES: readonly string[] = Object.values(LEGACY_CONTAINER)
/** Every name reserved at the drive root — new + legacy roots + legacy containers — for the create denylist. */
export const ALL_RESERVED_ROOT_NAMES: readonly string[] = [...NEW_ROOT_NAMES, ...LEGACY_ROOT_NAMES, ...LEGACY_CONTAINER_NAMES]

const NEW_ROOT_LC = new Set(NEW_ROOT_NAMES.map(lower))
const LEGACY_ROOT_LC = new Set(LEGACY_ROOT_NAMES.map(lower))
const ALL_RESERVED_LC = new Set(ALL_RESERVED_ROOT_NAMES.map(lower))

// Reserved-name checks trim AND lowercase the input so a leading/trailing-space
// variant (`" Library"`, `"Library "`) can't slip past the create/rename
// denylist and plant a folder that visually impersonates a reserved root.
const normReserved = (s: string): string => s.trim().toLowerCase()

/** Is `name` a reserved top-level folder (new OR legacy)? Case/space-insensitive. */
export function isReservedRootName(name: string): boolean {
  return ALL_RESERVED_LC.has(normReserved(name))
}

/**
 * #1119 I1 — the storage chokepoint's refusal for a reserved top-level folder (or the drive root) that someone tried to
 * delete, move or rename (`kind: 'source'`), or for a FOLDER that would land as one (`kind: 'destination'`). Matched by
 * `name` everywhere (the sync's safeguard list, the API error map, the move refusal lists), so `name` is set explicitly.
 */
export const RESERVED_ROOT_CODE = 'RESERVED_ROOT' as const
export class ReservedRootRefusedError extends Error {
  readonly code = RESERVED_ROOT_CODE
  readonly kind: 'source' | 'destination'
  /** The reserved folder name refused (`name` is the class's) — a Bin rebuild names it to the person (round 5). */
  readonly reservedName: string
  constructor(name: string, kind: 'source' | 'destination') {
    super(kind === 'source'
      ? `"${name}" is a system folder — it can't be deleted, moved or renamed`
      : `"${name}" is a reserved folder name`)
    this.kind = kind
    this.reservedName = name
    this.name = 'ReservedRootRefusedError'
  }
}

/**
 * Why a writer may delete / move / rename a reserved top-level folder, or rename a folder INTO a reserved name. A
 * REASON, never a boolean (the `WriteFreezeExemption` / `TenantModeExemption` pattern): only the hierarchy migrations
 * (`_system` → `System`, `MiniApps` → `PDApps`) pass it. It never covers the drive root itself.
 */
export type ReservedRootExemption = { reason: 'hierarchy-migration'; actor: 'worker' | 'migration' }

/**
 * #1119 I1 rule (ii), pure: is `node` a reserved top-level FOLDER — a reserved name (`isReservedRootName`) directly
 * under the drive root? Its parent is null (parity with the HTTP `isSystemFolder`), the canonical `papan_<tenant>`, or
 * — a pre-0011 tenant — a parentless folder named `papan` (`isDriveRootRow`). The canonical id is tested BEFORE
 * `parent`, so the caller reads the parent row only for a legacy root: it passes `parent = null` only when
 * `node.parentId` is null or canonical, else the row it read (null when there is none).
 */
export function isReservedTopLevelFolder(
  tenantId: string,
  node: { isFolder: boolean; name: string; parentId: string | null },
  parent: { id: string; parentId: string | null; name: string; isFolder: boolean } | null,
): boolean {
  if (!node.isFolder || !isReservedRootName(node.name)) return false
  if (node.parentId === null || isDriveRootId(tenantId, node.parentId)) return true
  return !!parent && parent.id === node.parentId && isDriveRootRow(tenantId, parent)
}

/** Is `name` one of the NEW top-level folders? Case/space-insensitive. */
export function isNewRootName(name: string): boolean {
  return NEW_ROOT_LC.has(normReserved(name))
}

/** Is `name` a LEGACY top-level folder (`_system` / `_shared`)? Case/space-insensitive. */
export function isLegacyRootName(name: string): boolean {
  return LEGACY_ROOT_LC.has(normReserved(name))
}

/**
 * Is `name` the standalone PDApp container — the new `PDApps` OR the pre-rename
 * `MiniApps`? Case/space-insensitive. Used where UI/nav must recognise the
 * container by name across the rename window (drive gate, nav-target lookup).
 */
export function isPdAppContainerName(name: string): boolean {
  const n = normReserved(name)
  return n === lower(ROOT.MINIAPPS) || n === lower(LEGACY_CONTAINER.MINIAPPS)
}

/**
 * New top-level folders that are RESTRICTED by default (seeded with
 * `files.restricted = true`). `Library` and `Projects` are open; the drive
 * root `/papan/` itself is also restricted but is handled at the root, not here.
 *
 * Restriction is applied at BOUNDARIES, never at descendants — contents inherit:
 *   - `Persons` (root) restricted → non-members see only the shell, not who
 *     has a space (can't enumerate members by browsing).
 *   - `Persons/<email>` (per-member) restricted + member-as-admin → each member
 *     sees/governs only their own. Seeded per member, not a "root" — see
 *     `personsSegments` — so it's not in this set but IS restricted.
 *   - Everything below a boundary (e.g. `Persons/<email>/Projects`) inherits and
 *     must NOT be flagged: the member's admin grant passes through, non-members
 *     resolve to `hidden` at the ancestor boundary.
 */
export const RESTRICTED_BY_DEFAULT_ROOTS: ReadonlySet<string> = new Set([
  ROOT.PERSONS,
  ROOT.SHARED,
  ROOT.SYSTEM,
])

/** Is the NEW top-level folder `name` restricted by default? Case-insensitive. */
export function isRestrictedByDefaultRoot(name: string): boolean {
  for (const r of RESTRICTED_BY_DEFAULT_ROOTS) {
    if (lower(r) === lower(name)) return true
  }
  return false
}

/**
 * Sub-folders seeded inside each member's `Persons/<email>` — only their
 * private `Projects`. Shares are NOT per-member: all inbound `.shared` pointers
 * live in the top-level `Shared/` (see `sharedContainerSegments`).
 */
export const PERSONS_CHILDREN: readonly string[] = [ROOT.PROJECTS, ROOT.MINIAPPS]

/** Path segments (under `/papan`) for an org member's private space root: `Persons/<email>`. */
export function personsSegments(email: string): string[] {
  return [ROOT.PERSONS, lower(email)]
}

/**
 * Path segments (under `/papan`) for the project container in the NEW
 * hierarchy. With `email` → an org member's private projects
 * (`Persons/<email>/Projects`); without → org-shared / personal projects
 * (`Projects`). Email is lower-cased to match the org-email identity key.
 */
export function projectContainerSegments(email?: string): string[] {
  return email
    ? [ROOT.PERSONS, lower(email), ROOT.PROJECTS]
    : [ROOT.PROJECTS]
}

/**
 * Path segments (under `/papan`) for the STANDALONE mini-apps container. With
 * `email` → an org member's private mini-apps (`Persons/<email>/MiniApps`);
 * without → org-shared / personal mini-apps (`MiniApps`). Mirrors
 * `projectContainerSegments`. (Mini-apps built FOR a project live inside that
 * project folder instead, not here.)
 */
export function miniAppsContainerSegments(email?: string): string[] {
  return email
    ? [ROOT.PERSONS, lower(email), ROOT.MINIAPPS]
    : [ROOT.MINIAPPS]
}

/**
 * LEGACY standalone-PDApp container segments — the pre-rename `MiniApps` name.
 * Used by the transitional dual-path resolver (findMiniAppsContainer) so a
 * tenant whose folder the rename sweep has not yet touched keeps resolving.
 * Mirrors `miniAppsContainerSegments`.
 */
export function legacyMiniAppsContainerSegments(email?: string): string[] {
  return email
    ? [ROOT.PERSONS, lower(email), LEGACY_CONTAINER.MINIAPPS]
    : [LEGACY_CONTAINER.MINIAPPS]
}

/**
 * Path segments (under `/papan`) for the top-level shares container — `Shared`,
 * the `_shared` replacement. Org-wide inbound `.shared` pointers land here
 * (per-pointer liaison admin governs visibility); shares are NOT per-member.
 */
export function sharedContainerSegments(): string[] {
  return [ROOT.SHARED]
}

/**
 * LEGACY project container segments — `_system/workspaces` (shared/personal) or
 * `_system/users/<email>/workspaces` (org-individual). Used by the transitional
 * dual-path resolver and the Stage 4 migration mapping.
 */
export function legacyProjectContainerSegments(email?: string): string[] {
  return email
    ? [LEGACY_ROOT.SYSTEM, LEGACY_USERS_SEGMENT, lower(email), LEGACY_WORKSPACES_SEGMENT]
    : [LEGACY_ROOT.SYSTEM, LEGACY_WORKSPACES_SEGMENT]
}

/**
 * The per-user Archangel system home — the trust boundary that hosts a user's
 * ambient AI-chat surfaces (`Papan AI.workspace`, etc.). Dot-prefixed so it
 * stays out of `bash ls` / serve (SPA may still show it as system plumbing).
 * See docs/plans/2026-07-12_WORKSPACE_ASSISTANT_SCOPED_SANDBOX.md §3.3.
 */
export const ARCHANGEL_FOLDER_NAME = '.Archangel'

/**
 * Path segments (under `/papan`) for a user's Archangel system home:
 *   org member  → `Persons/<email>/System/.Archangel`
 *   personal    → `System/.Archangel`
 * Mirrors the tenant hierarchy inside each person's space (a member's Persons
 * space is structurally a personal drive, so system plumbing lives under
 * `System/` in both). Email is lower-cased to match the org-email identity key.
 */
export function archangelSegments(email?: string): string[] {
  return email
    ? [ROOT.PERSONS, lower(email), ROOT.SYSTEM, ARCHANGEL_FOLDER_NAME]
    : [ROOT.SYSTEM, ARCHANGEL_FOLDER_NAME]
}
